import React, { useCallback, useEffect, useState } from "react";
import makeStyles from "@material-ui/core/styles/makeStyles";
import Box from "@material-ui/core/Box";
import { Trans } from "react-i18next";
import Button from "@material-ui/core/Button";
import LoginExistingUser from "./LoginExistingUser";
import LoginOrRegister from "./LoginOrRegister";
import EnterCode from "./EnterCode";
import BoxContainer from "./BoxContainer";
import { useAuth } from "../../providers/AuthProvider";
import BoxHeading from "./BoxHeading";
import NotReceivedCodeDialog from "./NotReceivedCodeDialog";
import UserLoggedInBox from "./UserLoggedInBox";
import { alpha } from "@material-ui/core/styles/colorManipulator";
import useTheme from "@material-ui/core/styles/useTheme";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: "#202529",
    display: "flex",
    flexGrow: 1,
    justifyContent: "center",
    width: "100%",
    overflow: "hidden",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
    },
  },
  pageContainer: {
    margin: "0 auto 0 auto",
    maxWidth: "1152px",
    flexGrow: 1,
    display: "flex",
    justifyContent: "flex-end",
    position: "relative",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      position: "fixed",
      top: 0,
      left: 0,
      height: "100vh",
      width: "100%",
      justifyContent: "flex-start",
      maxHeight: "-webkit-fill-available",
    },
  },
  backgroundContainer: {
    display: "flex",
    margin: "0 auto 0 auto",
    flexGrow: 1,
    position: "relative",
    flexShrink: 0,
    [theme.breakpoints.only("xs")]: {
      width: "100%",
    },
    ...(theme.clientType == "mobile" && {
      [theme.breakpoints.between("sm", "md")]: {
        width: "100%",
        height: "100%",
      },
    }),
  },
  backgroundInnerContainer: {
    position: "absolute",
    left: 0,
    top: 0,
    width: "100%",
    height: "100%",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "25% 100%",
    backgroundSize: "auto 100%",
    [`${theme.breakpoints.down("xs")}, ${theme.breakpoints.down(
      "md",
    )} and (orientation:portrait)`]: {
      height: "50vh",
      backgroundPosition: "14% 0%",
      backgroundSize: "auto 100%",
      //backgroundColor: 'red',
      //boxShadow: '0 0 8px 8px #202529 inset',
    },
    [theme.breakpoints.only("sm")]: {
      //backgroundPosition: '10% 100%',
    },
  },
  backgroundBottomShadow: {
    position: "absolute",
    left: 0,
    bottom: 0,
    width: "100%",
    height: "70px",
    background: `linear-gradient(180deg, ${alpha(
      window.PAGE_SETTINGS.background.color,
      0,
    )} 0%, ${window.PAGE_SETTINGS.background.color} 100%)`,
  },
  formContainer: {
    flexBasis: "480px",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down("xs")]: {
      flexBasis: "100px",
      marginRight: theme.spacing(3),
      marginLeft: theme.spacing(3),
      paddingBottom: 0,
      marginTop: 0,
      marginBottom: theme.spacing(3),
    },
  },
  button: {
    minWidth: "136px",
    paddingRight: 0,
    paddingLeft: 0,
    [theme.breakpoints.down("xs")]: {
      minWidth: "96px",
    },
  },
  spacer: {},
  columnSpacer: {
    flexGrow: 1,
    [theme.breakpoints.down("xs")]: {
      flexGrow: 0,
      flexBasis: "50vh",
      flexShrink: 1,
    },
  },
  bottomContainer: {
    marginTop: theme.spacing(4),
  },

  alreadyHaveAccountButton: {
    fontSize: "20px",
    marginTop: 0,
    marginBottom: 0,
  },
}));

const FrontPage = ({ handleLoginSuccess, handleLoginSuccessNewCode }) => {
  const classes = useStyles();
  const [state, setState] = useState("");
  const { isAuthenticated } = useAuth();

  const [codeData, setCodeData] = useState(() => {
    // If we pass code data from the script:
    const codeData = window.PROPHOTO_CODE_DATA;
    delete window.PROPHOTO_CODE_CODE;
    return codeData;
  });
  const [displayNotReceivedCodeDialog, setDisplayNotReceivedCodeDialog] =
    useState(false);
  const theme = useTheme();

  console.log("Theme client", theme.client, theme);

  const setStateByHash = (hash) => {
    console.log("The hash in useEffect is", hash);
    if (hash === "#login") {
      setState("login");
      // Clear hash so we don't go to login state again
      history.replaceState(null, null, "#");
    }
  };

  useEffect(() => {
    if (codeData && !codeData.error) {
      setState("login-or-register");
    } else {
      setState("");
    }
  }, [codeData]);

  useEffect(() => {
    if (window.location.hash) {
      setStateByHash(window.location.hash);
    }
  }, [window.location.hash]);

  const hashChangeHandler = useCallback(() => {
    console.log("Handle hash change", window.location.hash);
    setStateByHash(window.location.hash);
  }, []);

  // Hash change listener only works on desktop. May be related
  // to react-router on mobile?
  useEffect(() => {
    window.addEventListener("hashchange", hashChangeHandler);
    return () => {
      window.removeEventListener("hashchange", hashChangeHandler);
    };
  }, []);

  console.log("The hash is", window.location.hash);

  const handleSetStateLogin = () => {
    setState("login");
  };

  const { background } = window.PAGE_SETTINGS;

  console.log("Page settings", background);

  return (
    <div
      className={classes.root}
      style={{ backgroundColor: background.color }}
    >
      <div className={classes.backgroundContainer}>
        <div
          className={classes.backgroundInnerContainer}
          style={{ backgroundImage: `url(${background.image}` }}
        >
          {theme.clientType != "desktop" && (
            <div className={classes.backgroundBottomShadow} />
          )}
        </div>
        <div className={classes.pageContainer}>
          <div className={classes.columnSpacer} />
          <div className={classes.formContainer}>
            <div className={classes.spacer} />
            {state == "login" && (
              <LoginExistingUser
                handleLoginSuccess={handleLoginSuccess}
                handleGoBack={() => setState("")}
              />
            )}
            {state == "login-or-register" && (
              <LoginOrRegister
                codeInfo={codeData}
                handleLoginSuccess={handleLoginSuccessNewCode}
                handleGoBack={() => setState("")}
              />
            )}
            {state == "" && (
              <>
                <EnterCode
                  handleSetCodeData={(data) => setCodeData(data)}
                  setDisplayNotReceivedCodeDialog={
                    setDisplayNotReceivedCodeDialog
                  }
                  // If we're authenticated, checking a code will log you in:
                  handleLoginSuccess={handleLoginSuccessNewCode}
                  codeData={codeData}
                />
                {isAuthenticated && <UserLoggedInBox />}
                {!isAuthenticated && (
                  <>
                    <BoxContainer className={classes.bottomContainer}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <BoxHeading
                          className={classes.alreadyHaveAccountButton}
                        >
                          <Trans>Already have an account?</Trans>
                        </BoxHeading>
                        <Button
                          variant="contained"
                          color="primary"
                          size={"large"}
                          classes={{ root: classes.button }}
                          onClick={handleSetStateLogin}
                        >
                          <Trans>Sign in</Trans>
                        </Button>
                      </Box>
                    </BoxContainer>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {displayNotReceivedCodeDialog && (
        <NotReceivedCodeDialog
          onClose={() => setDisplayNotReceivedCodeDialog(false)}
        />
      )}
    </div>
  );
};

export default FrontPage;
